// Firebase Authentication Functions
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  updatePassword,
  updateProfile,
} from "firebase/auth";

// Firebase Realtime Database Functions
import { ref, get, set } from "firebase/database";

// Firebase App Initialization
import { auth, database } from "./firebase"; // Ensure firebase.js properly exports auth and database

// Create a new user with email and password
export const doCreateUserWithEmailAndPassword = async (email, password) => {
  return createUserWithEmailAndPassword(auth, email, password);
};

// Sign in with email and password
export const doSignInWithEmailAndPassword = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    // Force Firebase to refresh the user's data
    await user.reload();

    // Check if the email is verified
    if (!user.emailVerified) {
      await auth.signOut();
      throw new Error(
        "Your email is not verified. Please verify your email before signing in."
      );
    }

    return userCredential; // Proceed with the authenticated user
  } catch (error) {
    console.error("Sign-in error:", error.message);
    throw error; // Pass the error for external handling
  }
};

// Sign out the current user
export const doSignOut = () => {
  return auth.signOut();
};

// Send a password reset email
export const doPasswordReset = (email) => {
  return sendPasswordResetEmail(auth, email);
};

// Update the current user's password
export const doPasswordChange = async (password) => {
  if (!auth.currentUser) {
    throw new Error("No authenticated user found.");
  }
  return updatePassword(auth.currentUser, password);
};

// Send an email verification link
export const doSendEmailVerification = async () => {
  if (!auth.currentUser) {
    throw new Error("No authenticated user found for email verification.");
  }
  try {
    await sendEmailVerification(auth.currentUser);
    console.log("Verification email sent!");
  } catch (error) {
    console.error("Error Code:", error.code);
    console.error("Error Message:", error.message);
    console.error("Full Error:", error);
    throw error;
  }
};

// Update the user's profile (e.g., displayName)
export const doUpdateProfile = async (user, { displayName }) => {
  if (!user) {
    throw new Error("No user found for profile update.");
  }
  return updateProfile(user, { displayName });
};

// Check if a username is unique
export const doCheckUsernameUnique = async (username) => {
  const usernameRef = ref(database, `usernames/${username}`);
  const snapshot = await get(usernameRef);
  return !snapshot.exists();
};

// Write user data to Realtime Database
export const doSetUserData = async (userId, userData) => {
  const userRef = ref(database, `users/${userId}`);
  await set(userRef, userData);
};

// Export database and ref for use in other components
export { database, ref as dbRef, set };
